/**
 * Building a position from a coordinate map.
 *
 * Used by tests, by the perft golden suite and — from Phase 4 — by the tutorial, which
 * sets up teaching positions the same way. Cells are named by `"q,r,s"` key, never by
 * index, so a setup literal is readable and survives any re-indexing of the board.
 *
 * The piece codes follow the 2020 tutorial script's convention closely enough to be
 * familiar, but spelled unambiguously: `k0` `p0` `k1` `p1`.
 */

import { getBoard } from './board.ts';
import { parseKey } from './hex.ts';
import type { Piece, PlayerIndex, Position, Variant } from './types.ts';

export type PieceCode = 'k0' | 'p0' | 'k1' | 'p1';

const CODES: Readonly<Record<PieceCode, Piece>> = {
  k0: { player: 0, type: 'king' },
  p0: { player: 0, type: 'pawn' },
  k1: { player: 1, type: 'king' },
  p1: { player: 1, type: 'pawn' },
};

export const pieceFromCode = (code: PieceCode): Piece => {
  const piece = CODES[code];
  if (!piece) throw new Error(`Unknown piece code ${JSON.stringify(code)}`);
  return piece;
};

export const codeFromPiece = (piece: Piece): PieceCode =>
  `${piece.type === 'king' ? 'k' : 'p'}${piece.player}` as PieceCode;

/** A position described by hand: which pieces sit where, whose turn, spawns in hand. */
export interface PositionSetup {
  readonly variant: Variant;
  readonly playerToMove: PlayerIndex;
  readonly spawnsAvailable: readonly [number, number];
  /** Piece by `"q,r,s"` coordinate key. Cells not listed are empty. */
  readonly pieces: Readonly<Record<string, PieceCode>>;
}

/** Build a `Position` from a setup literal, validating every coordinate. */
export function makePosition(setup: PositionSetup): Position {
  const board = getBoard(setup.variant.radius);
  const cells: (Piece | null)[] = new Array(board.size).fill(null);

  for (const [k, code] of Object.entries(setup.pieces)) {
    parseKey(k); // throws on a malformed key before the friendlier message below
    const index = board.indexOf(k);
    if (index === undefined) {
      throw new Error(
        `Setup places ${code} at ${k}, which is off a radius-${setup.variant.radius} board`,
      );
    }
    if (cells[index] !== null) {
      throw new Error(`Setup places two pieces at ${k}`);
    }
    cells[index] = pieceFromCode(code);
  }

  return {
    variant: setup.variant,
    playerToMove: setup.playerToMove,
    cells,
    spawnsAvailable: [setup.spawnsAvailable[0], setup.spawnsAvailable[1]],
  };
}

/** The inverse: describe a position as a coordinate map. Handy in failure messages. */
export function describePosition(position: Position): Record<string, PieceCode> {
  const board = getBoard(position.variant.radius);
  const out: Record<string, PieceCode> = {};
  position.cells.forEach((piece, i) => {
    if (piece) out[board.keyAt(i)] = codeFromPiece(piece);
  });
  return out;
}
